import { motion } from "framer-motion";

/**
 * Premium animated SVG world map with pulsing trade-flow arcs.
 * Pure CSS/SVG — no external libs. Designed to feel like a Bloomberg / Stripe
 * data-vis backdrop without being noisy.
 */
export function TradeFlowGlobe() {
  // Five flagship corridors — start (origin) → end (destination)
  const corridors = [
    { id: "in-us", from: { x: 660, y: 240 }, to: { x: 220, y: 215 }, label: "India → USA" },
    { id: "vn-us", from: { x: 750, y: 260 }, to: { x: 220, y: 215 }, label: "Vietnam → USA" },
    { id: "no-us", from: { x: 510, y: 145 }, to: { x: 220, y: 215 }, label: "Norway → USA" },
    { id: "in-ae", from: { x: 660, y: 250 }, to: { x: 580, y: 250 }, label: "India → UAE" },
    { id: "au-cn", from: { x: 830, y: 380 }, to: { x: 760, y: 230 }, label: "Australia → China" },
  ];

  const arcPath = (a, b) => {
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2 - Math.abs(a.x - b.x) * 0.18;
    return `M ${a.x},${a.y} Q ${mx},${my} ${b.x},${b.y}`;
  };

  return (
    <svg
      viewBox="0 0 1000 500"
      className="absolute inset-0 w-full h-full pointer-events-none"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
      data-testid="trade-flow-globe"
    >
      <defs>
        <radialGradient id="bgGlow" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#22C55E" stopOpacity="0.05" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <linearGradient id="arcGradient" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#22C55E" stopOpacity="0" />
          <stop offset="50%" stopColor="#22C55E" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#22C55E" stopOpacity="0" />
        </linearGradient>
        <filter id="dotGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
        </filter>
      </defs>

      <rect width="1000" height="500" fill="url(#bgGlow)" />

      {/* Continent dot grid — subtle, hand-tuned to suggest a world map */}
      <g fill="#22C55E" opacity="0.18">
        {/* North America */}
        {[
          [120, 180], [140, 170], [160, 175], [180, 185], [200, 195], [220, 205], [240, 215],
          [160, 200], [180, 210], [200, 220], [220, 230], [240, 240], [260, 250],
          [200, 245], [220, 255], [240, 265], [260, 275], [280, 285], [300, 295],
          [240, 290], [260, 300], [280, 310], [300, 320], [320, 330],
        ].map(([x, y], i) => <circle key={`na-${i}`} cx={x} cy={y} r="1.4" />)}
        {/* South America */}
        {[
          [300, 340], [320, 350], [340, 360], [360, 370],
          [320, 380], [340, 390], [360, 400], [340, 420], [360, 430],
        ].map(([x, y], i) => <circle key={`sa-${i}`} cx={x} cy={y} r="1.4" />)}
        {/* Europe */}
        {[
          [490, 130], [510, 140], [530, 145], [550, 150], [510, 160], [530, 170], [490, 175],
          [470, 165], [450, 175], [510, 180], [530, 195],
        ].map(([x, y], i) => <circle key={`eu-${i}`} cx={x} cy={y} r="1.4" />)}
        {/* Africa */}
        {[
          [510, 220], [530, 235], [550, 250], [530, 270], [550, 290],
          [510, 290], [530, 310], [550, 330], [530, 350], [510, 370],
        ].map(([x, y], i) => <circle key={`af-${i}`} cx={x} cy={y} r="1.4" />)}
        {/* Middle East */}
        {[
          [580, 230], [600, 240], [620, 250], [580, 250], [600, 260],
        ].map(([x, y], i) => <circle key={`me-${i}`} cx={x} cy={y} r="1.4" />)}
        {/* Asia */}
        {[
          [640, 200], [660, 210], [680, 220], [700, 230], [720, 240], [740, 250], [760, 260],
          [660, 230], [680, 240], [700, 250], [720, 260], [740, 270],
          [620, 220], [640, 230], [660, 250], [680, 260], [780, 270], [800, 280],
        ].map(([x, y], i) => <circle key={`as-${i}`} cx={x} cy={y} r="1.4" />)}
        {/* Australia */}
        {[
          [810, 360], [830, 370], [850, 380], [810, 390], [830, 400], [850, 410],
        ].map(([x, y], i) => <circle key={`au-${i}`} cx={x} cy={y} r="1.4" />)}
      </g>

      {/* Static arc lines */}
      {corridors.map((c) => (
        <path
          key={`arc-${c.id}`}
          d={arcPath(c.from, c.to)}
          stroke="#22C55E"
          strokeOpacity="0.18"
          strokeWidth="1"
          fill="none"
        />
      ))}

      {/* Animated pulse arcs */}
      {corridors.map((c, i) => (
        <path
          key={`pulse-${c.id}`}
          d={arcPath(c.from, c.to)}
          stroke="url(#arcGradient)"
          strokeWidth="2"
          fill="none"
          strokeDasharray="6 200"
          strokeDashoffset="0"
          style={{ animation: `dash-flow 5s ${i * 0.7}s ease-in-out infinite` }}
        />
      ))}

      {/* Origin/destination glow dots */}
      {corridors.map((c) => (
        <g key={`dot-${c.id}`}>
          <circle cx={c.from.x} cy={c.from.y} r="6" fill="#22C55E" opacity="0.25" filter="url(#dotGlow)" />
          <circle cx={c.from.x} cy={c.from.y} r="2.2" fill="#22C55E">
            <animate attributeName="opacity" values="1;0.4;1" dur="3s" repeatCount="indefinite" />
          </circle>
          <circle cx={c.to.x} cy={c.to.y} r="6" fill="#22C55E" opacity="0.25" filter="url(#dotGlow)" />
          <circle cx={c.to.x} cy={c.to.y} r="2.2" fill="#22C55E">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="3s" repeatCount="indefinite" />
          </circle>
        </g>
      ))}

      <style>{`
        @keyframes dash-flow {
          0%   { stroke-dashoffset: 200; opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { stroke-dashoffset: -200; opacity: 0; }
        }
      `}</style>
    </svg>
  );
}

/**
 * Live counter bar — animates numbers from 0 to target on first render.
 */
export function AnimatedCounter({ value, suffix = "", prefix = "", duration = 1.4 }) {
  // Lightweight CSS-only animation using --to custom property + counter()
  // Falls back gracefully on browsers that don't support @property.
  return (
    <motion.span
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      {prefix}{value}{suffix}
    </motion.span>
  );
}
